import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Textarea } from '@/components/ui/textarea';

interface ReturnIntakeDialogProps {
  isSubmitting: boolean;
  onConfirm: (reviewNote: string) => void;
  onCancel: () => void;
}

export function ReturnIntakeDialog({ isSubmitting, onConfirm, onCancel }: ReturnIntakeDialogProps) {
  const { t } = useTranslation(['intake', 'common']);
  const [reviewNote, setReviewNote] = useState('');
  const isValid = reviewNote.trim().length > 0;

  return (
    <ModalOverlay onRequestClose={onCancel}>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-zoowarningbg">
          <Icon name="AlertTriangle" className="text-xl text-zoowarningtext" />
        </div>
        <h2 className="text-lg font-bold text-zooindigo">{t('intake:reviewSection.returnDialogTitle')}</h2>
        <p className="text-sm text-zoomuted mt-1.5">{t('intake:reviewSection.returnDialogHint')}</p>

        <div className="space-y-1.5 mt-4">
          <Label htmlFor="intake-return-note">{t('intake:reviewSection.returnNoteLabel')}</Label>
          <Textarea
            id="intake-return-note"
            rows={4}
            value={reviewNote}
            placeholder={t('intake:reviewSection.returnNotePlaceholder')}
            onChange={function update(changeEvent) {
              setReviewNote(changeEvent.target.value);
            }}
          />
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            className="flex-1"
            disabled={isValid === false}
            isLoading={isSubmitting}
            onClick={function confirm() {
              onConfirm(reviewNote.trim());
            }}
          >
            {t('intake:reviewSection.returnConfirm')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
